import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../constants/track-events'
import { useSampleDataDialog } from '../../context/sample-data-context'
import { trackEvent } from '../../helpers/analytics'
import { getFocusTargetAfterRemoval, isFocusVisible } from '../../helpers/focus'
import { SAMPLE_DATA_AVAILABILITY } from '../../helpers/sample-data'
import {
  useSampleDataAvailability,
  useSampleDataHintDismissal
} from '../../hooks/use-sample-data'
import InfoCallout from '../tailwind/InfoCallout'

const { NONE, PRESENT, NO_PERMISSION, ONBOARDING, TEMPLATE_PARTNER } =
  SAMPLE_DATA_AVAILABILITY

/**
 * Hinweis zwischen PageHeading und Liste, solange Beispieldaten da sind.
 * Per X bis zum nächsten Reload ausblendbar, für alle Listen des Partners.
 * Entfernen bleibt danach über das Optionsmenü der Liste und die Settings
 * erreichbar. Ohne Löschrecht oder im Discover-Onboarding steht statt der
 * Aktion ein Satz.
 * spaces: geladene Talente der Seite (Stellen liest der Hook selbst).
 * source: Einstieg fürs Tracking (hint_careers, hint_talents, hint_recover).
 */
const SampleDataHint = ({ spaces, source, className }) => {
  const { t } = useTranslation()
  const { openSampleDataDialog } = useSampleDataDialog()
  const { availability } = useSampleDataAvailability({ spaces })
  const { isDismissed, dismiss } = useSampleDataHintDismissal()
  const wrapperRef = useRef(null)

  if (availability === NONE || availability === TEMPLATE_PARTNER) return null
  if (isDismissed) return null

  const suffix = {
    [NO_PERMISSION]: t('sample_data.hint.no_permission'),
    [ONBOARDING]: t('sample_data.hint.onboarding')
  }[availability]

  const handleDismiss = (event) => {
    // Per Tastatur geschlossen geht der Fokus zum benachbarten Bedienelement
    // statt an den Seitenanfang. Nach Klick oder Tap nicht, sonst öffnet ein
    // Suchfeld auf Touch-Geräten die Tastatur.
    const next = isFocusVisible(event?.currentTarget)
      ? getFocusTargetAfterRemoval(wrapperRef.current)
      : null

    trackEvent(trackEvents.SAMPLE_DATA_HINT_DISMISSED, { source, availability })
    next?.focus()
    dismiss()
  }

  return (
    <div ref={wrapperRef} className={className}>
      <InfoCallout
        tone='neutral'
        onDismiss={handleDismiss}
        dismissLabel={t('sample_data.hint.dismiss')}
        action={
          availability === PRESENT ? (
            <button
              type='button'
              onClick={() => openSampleDataDialog({ mode: 'remove', source })}
              className='whitespace-nowrap rounded font-medium text-primaryBlue hover:text-primaryBlue/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primaryBlue'
            >
              {t('sample_data.hint.action')}
            </button>
          ) : undefined
        }
      >
        {t('sample_data.hint.text')}
        {suffix && ` ${suffix}`}
      </InfoCallout>
    </div>
  )
}

SampleDataHint.propTypes = {
  spaces: PropTypes.array,
  source: PropTypes.string.isRequired,
  className: PropTypes.string
}

SampleDataHint.defaultProps = {
  spaces: undefined,
  // Abstand wie die Filterleiste der Tabellen darunter
  className: 'px-4 pt-4 md:px-6 lg:px-8'
}

export default SampleDataHint
